import { motion, useReducedMotion, useScroll, useTransform } from "motion/react";
import { useRef } from "react";
import { ArrowDown } from "lucide-react";
import { HeroPhoto } from "../OptimizedImage";
import { ResortLink } from "../resort-button";

export function NearbyHero() {
  const ref = useRef<HTMLElement>(null);
  const reduced = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "12%"]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "-12%"]);
  const ease = [0.22, 1, 0.36, 1] as const;

  return (
    <section
      ref={ref}
      id="top"
      aria-labelledby="nearby-hero-heading"
      className="relative min-h-[78svh] overflow-hidden bg-forest-deep md:min-h-[76svh]"
    >
      <motion.div
        className="absolute inset-0"
        style={{ y: reduced ? 0 : bgY }}
        initial={reduced ? false : { scale: 1.04 }}
        animate={{ scale: 1 }}
        transition={{ duration: 2.4, ease }}
      >
        <HeroPhoto alt="Sunlit Sierra Foothills trail above the American River near Auburn California" />
        <div className="overlay-cinematic absolute inset-0" />
      </motion.div>

      <motion.div
        style={{ y: reduced ? 0 : contentY }}
        className="shell relative flex min-h-[78svh] flex-col justify-end pb-18 pt-18 md:min-h-[76svh] md:pb-20 md:pt-20"
      >
        <motion.p
          className="eyebrow text-primary-foreground/80"
          initial={reduced ? false : { opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.2, ease }}
        >
          Explore Northern California
        </motion.p>

        <h1
          id="nearby-hero-heading"
          className="display-xl mt-6 max-w-[15ch] text-primary-foreground"
        >
          {["Adventure Starts", "Just Beyond", "the Resort"].map((line, i) => (
            <span key={line} className="block overflow-hidden">
              <motion.span
                className="display-xl block"
                initial={reduced ? false : { y: "112%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 1.15, delay: 0.35 + i * 0.1, ease }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          className="mt-8 max-w-2xl text-base leading-relaxed text-primary-foreground/80 md:text-lg"
          initial={reduced ? false : { opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.85, ease }}
        >
          Hike the Sierra Foothills, explore California Gold Country, take on the American River or
          spend the day discovering historic towns, local flavors and unforgettable mountain
          scenery.
        </motion.p>

        <motion.div
          className="mt-10 flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:flex-wrap"
          initial={reduced ? false : { opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.05, ease }}
        >
          <ResortLink variant="gold" href="/reservations/" className="w-full sm:w-auto">
            Plan Your Stay
          </ResortLink>
          <ResortLink variant="onDark" href="#destination-explorer" className="w-full sm:w-auto">
            Explore the Area
          </ResortLink>
        </motion.div>

        <motion.p
          className="eyebrow mt-6 text-primary-foreground/60"
          initial={reduced ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.2, ease }}
        >
          Auburn · American River · Gold Country · Sierra Foothills
        </motion.p>
      </motion.div>

      <div className="pointer-events-none absolute bottom-10 right-6 hidden items-center gap-3 text-primary-foreground/70 md:flex">
        <span className="eyebrow">Scroll</span>
        <motion.span
          animate={reduced ? {} : { y: [0, 8, 0] }}
          transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
        >
          <ArrowDown className="size-4" strokeWidth={1.5} />
        </motion.span>
      </div>
    </section>
  );
}
